import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { usePublicConfig } from '../lib/config';
import { Alert } from './ui';
import { APPLE_REDIRECT_URI, APPLE_SERVICE_ID, GOOGLE_CLIENT_ID } from '../lib/env';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize(opts: {
            client_id: string;
            callback: (r: { credential: string }) => void;
            nonce?: string;
            ux_mode?: 'popup';
          }): void;
          renderButton(el: HTMLElement, opts: Record<string, unknown>): void;
        };
      };
    };
    AppleID?: {
      auth: {
        init(opts: Record<string, unknown>): void;
        signIn(): Promise<{ authorization: { id_token: string } }>;
      };
    };
  }
}

const GOOGLE_SRC = 'https://accounts.google.com/gsi/client';
const APPLE_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

function loadScript(src: string) {
  return new Promise<void>((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const el = document.createElement('script');
    el.src = src;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error(`Could not load ${src}`));
    document.head.appendChild(el);
  });
}

/** A random nonce: the raw value goes to our server, its SHA-256 to the provider (replay protection). */
export async function makeNonce() {
  const raw = Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) =>
    b.toString(16).padStart(2, '0'),
  ).join('');
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
  const hashed = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join(
    '',
  );
  return { raw, hashed };
}

/**
 * Google and Apple buttons. Each appears only when this build has the provider's public client
 * id AND the server says it can verify that provider — otherwise nothing is shown rather than
 * a button that can't work.
 */
export function SocialSignIn({
  inviteCode,
  onSignedIn,
}: {
  inviteCode?: string;
  onSignedIn: (created: boolean) => void;
}) {
  const { signInWithProvider } = useAuth();
  const config = usePublicConfig();
  const googleId = GOOGLE_CLIENT_ID;
  const appleId = APPLE_SERVICE_ID;
  const google = !!googleId && !!config.data?.authProviders?.google;
  const apple = !!appleId && !!config.data?.authProviders?.apple;
  const googleEl = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const finish = async (provider: 'google' | 'apple', idToken: string, nonce?: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await signInWithProvider(provider, {
        idToken,
        nonce,
        inviteCode: inviteCode || undefined,
      });
      onSignedIn(r.created);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign-in failed. Try again.');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!google || !googleEl.current) return;
    let cancelled = false;
    void (async () => {
      try {
        const nonce = await makeNonce();
        await loadScript(GOOGLE_SRC);
        if (cancelled || !window.google || !googleEl.current) return;
        window.google.accounts.id.initialize({
          client_id: googleId!,
          nonce: nonce.hashed,
          ux_mode: 'popup',
          callback: (r) => void finish('google', r.credential, nonce.raw),
        });
        window.google.accounts.id.renderButton(googleEl.current, {
          theme: 'filled_black',
          size: 'large',
          width: 320,
          text: 'continue_with',
          shape: 'pill',
        });
      } catch {
        setError("Google sign-in couldn't load. Use email and password instead.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [google, googleId, inviteCode]); // eslint-disable-line react-hooks/exhaustive-deps

  async function appleSignIn() {
    try {
      const nonce = await makeNonce();
      await loadScript(APPLE_SRC);
      window.AppleID!.auth.init({
        clientId: appleId,
        scope: 'name email',
        redirectURI: APPLE_REDIRECT_URI ?? `${location.origin}/login`,
        usePopup: true,
        nonce: nonce.hashed,
      });
      const r = await window.AppleID!.auth.signIn();
      await finish('apple', r.authorization.id_token, nonce.raw);
    } catch (err) {
      // Closing the popup is not an error worth showing.
      if (!String((err as { error?: string })?.error ?? err).includes('popup_closed'))
        setError('Apple sign-in did not complete.');
    }
  }

  if (!google && !apple) return null;
  return (
    <div className="space-y-3" data-testid="social-sign-in">
      {error ? <Alert>{error}</Alert> : null}
      {google ? (
        <div ref={googleEl} className="flex justify-center" aria-label="Continue with Google" />
      ) : null}
      {apple ? (
        <button
          type="button"
          onClick={() => void appleSignIn()}
          disabled={busy}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-black px-4 py-2.5 text-sm font-semibold text-white ring-1 ring-white/20 disabled:opacity-60"
        >
          <span aria-hidden></span> Continue with Apple
        </button>
      ) : null}
      <div className="flex items-center gap-3 text-xs text-slate">
        <span className="h-px flex-1 bg-overlay/10" /> or with email{' '}
        <span className="h-px flex-1 bg-overlay/10" />
      </div>
    </div>
  );
}
